import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Tag} from "primereact/tag";
import {ProgressBar} from "primereact/progressbar";
import MarkdownEditor from "../posts/markdown-editor";
import {uploadHelpMedia} from "./help-media-api";
import {
    APP_ANCHORS,
    DEFAULT_SURFACES,
    STATUS_LABELS,
    SURFACES,
    TYPE_LABELS,
    isVideo,
    newSlug,
    tagsOf,
    videoOfArticle,
} from "./help-cms";

const EMPTY = {
    contentType: "article",
    categorySlug: null,
    title: "",
    summary: "",
    tags: "",
    body: "",
    sections: "",
    readTime: "",
    level: "مقدماتی",
    videoUrl: "",
    duration: "",
    chapters: "",
    relatedSlug: null,
    appAnchors: [],
    surfaces: DEFAULT_SURFACES,
};
const LEVELS = ["مقدماتی", "متوسط", "پیشرفته"];
const STEP_TITLES = ["۱. اطلاعات اصلی", "۲. ارتباط و نمایش", "۳. بازبینی و انتشار"];

const splitTags = (value) => [...new Set(String(value || "").split(/[،,]/).map((tag) => tag.trim()).filter(Boolean))];
const splitLines = (value) => String(value || "").split("\n").map((line) => line.trim()).filter(Boolean);
const parseChapters = (value) => splitLines(value).map((line) => {
    const [title, time] = line.split("|").map((part) => part.trim());
    return [title, time || "۰۰:۰۰"];
});

function toForm(item, contents, prefillTitle) {
    if (!item) return {...EMPTY, title: prefillTitle || ""};
    const video = !isVideo(item) ? videoOfArticle(contents, item) : null;
    return {
        contentType: item.contentType,
        categorySlug: item.categorySlug || null,
        title: item.title || "",
        summary: item.summary || "",
        tags: tagsOf(item).join("، "),
        body: item.body || "",
        sections: (item.sections || []).join("\n"),
        readTime: item.readTime || "",
        level: item.level || "مقدماتی",
        videoUrl: item.videoUrl || "",
        duration: item.duration || "",
        chapters: (item.chapters || []).map(([title, time]) => `${title} | ${time}`).join("\n"),
        // article -> slug of the complementary video, video -> slug of its article
        relatedSlug: isVideo(item) ? item.relatedSlug || null : video?.slug || null,
        appAnchors: Array.isArray(item.appAnchors) ? item.appAnchors : [],
        surfaces: {...DEFAULT_SURFACES, ...(item.surfaces || {})},
    };
}

function Field({label, hint, full = true, children}) {
    return (
        <div className={`flex flex-column gap-1 ${full ? "col-12" : "col-12 md:col-6"}`}>
            <label className="text-xs text-600">{label}</label>
            {children}
            {hint && <small className="text-400">{hint}</small>}
        </div>
    );
}

/**
 * Three step editor from the design: main info → relation and display → review and publish.
 * `item` null = new content; `prefillTitle` is used when creating content from an unanswered search.
 */
export default function ContentEditorDialog({visible, item, prefillTitle, data, onHide, onSaved}) {
    const {contents, categories, tags: knownTags, api} = data;
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const videoFileRef = useRef(null);
    const [videoUpload, setVideoUpload] = useState({busy: false, progress: 0, error: ""});

    useEffect(() => {
        if (!visible) return;
        setStep(1);
        setError("");
        setForm(toForm(item, contents, prefillTitle));
        // Reset only when the dialog opens for another content.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, item?.documentId, prefillTitle]);

    const set = (field) => (value) => setForm((current) => ({...current, [field]: value}));

    const onVideoFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setVideoUpload({busy: true, progress: 0, error: ""});
        try {
            const uploaded = await uploadHelpMedia(file, {onProgress: (progress) => setVideoUpload((current) => ({...current, progress}))});
            set("videoUrl")(uploaded.url);
            setVideoUpload({busy: false, progress: 100, error: ""});
        } catch (e) {
            const message = e?.response?.data?.error?.message;
            setVideoUpload({busy: false, progress: 0, error: message === "help-media.error.file-too-large" ? "حجم فایل بیشتر از حد مجاز است." : message === "help-media.error.unsupported-type" ? "فقط فایل mp4 یا webm مجاز است." : "آپلود ویدئو انجام نشد."});
        }
    };
    const video = form.contentType === "video";
    const category = categories.find((c) => c.slug === form.categorySlug);

    // Complement choices: an article picks a video, a video picks an article.
    const relationOptions = useMemo(() => contents
        .filter((c) => c.documentId !== item?.documentId && (video ? !isVideo(c) : isVideo(c)))
        .map((c) => ({value: c.slug, label: `${isVideo(c) ? "▶" : "▤"} ${c.title}`})), [contents, item, video]);

    const next = () => {
        if (step === 1 && (!form.title.trim() || !form.categorySlug)) {
            setError("عنوان و دسته اصلی الزامی است.");
            return;
        }
        setError("");
        setStep((current) => Math.min(3, current + 1));
    };

    const save = async (contentStatus) => {
        setSaving(true);
        setError("");
        try {
            const tagList = splitTags(form.tags);
            const slug = item?.slug || newSlug();
            const payload = {
                slug,
                contentType: form.contentType,
                title: form.title.trim(),
                summary: form.summary.trim(),
                body: video ? "" : form.body,
                bodyFormat: video ? "text" : "markdown",
                sections: video ? [] : splitLines(form.sections),
                readTime: video ? "" : form.readTime.trim(),
                level: form.level,
                videoUrl: video ? form.videoUrl.trim() : "",
                duration: video ? form.duration.trim() : "",
                chapters: video ? parseChapters(form.chapters) : [],
                categorySlug: form.categorySlug,
                tags: tagList,
                keywords: [form.title, ...tagList].join(" "),
                relatedSlug: video ? form.relatedSlug || null : item?.relatedSlug || null,
                surfaces: form.surfaces,
                appAnchors: form.appAnchors,
                contentStatus,
            };
            const saved = item ? await api.updateContent(item, payload) : await api.createContent(payload);

            // tags typed here become part of the tag list
            const existing = new Set(knownTags.map((tag) => tag.title));
            for (const title of tagList.filter((tag) => !existing.has(tag))) {
                await api.createTag({title, isPopular: false, sortOrder: knownTags.length});
            }

            // article side of the relation: move the "points at this article" link between videos
            if (!video) {
                const current = videoOfArticle(contents, {slug});
                if (current && current.slug !== form.relatedSlug) await api.updateContent(current, {relatedSlug: null});
                const target = contents.find((c) => c.slug === form.relatedSlug);
                if (target && target.relatedSlug !== slug) await api.updateContent(target, {relatedSlug: slug});
            }
            onSaved?.(saved);
            onHide();
        } catch (e) {
            setError(e?.response?.data?.error?.message || "ذخیره محتوا انجام نشد.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!item || !window.confirm("این محتوا حذف شود؟")) return;
        setSaving(true);
        try {
            await api.deleteContent(item);
            onSaved?.();
            onHide();
        } catch (e) {
            setError("حذف محتوا انجام نشد.");
        } finally {
            setSaving(false);
        }
    };

    const step1 = (
        <div className="grid">
            <Field label="نوع محتوا" full={false}>
                <Dropdown className="w-full" panelClassName="rtl" value={form.contentType} onChange={(e) => set("contentType")(e.value)}
                          options={Object.entries(TYPE_LABELS).map(([value, label]) => ({value, label}))}/>
            </Field>
            <Field label="دسته اصلی" full={false}>
                <Dropdown className="w-full" panelClassName="rtl" value={form.categorySlug} placeholder="انتخاب کنید" onChange={(e) => set("categorySlug")(e.value)}
                          options={categories.map((c) => ({value: c.slug, label: c.title}))}/>
            </Field>
            <Field label="عنوان" hint="همان عبارتی که کاربر جست‌وجو می‌کند">
                <InputText value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder="عنوان محتوا"/>
            </Field>
            <Field label="توضیح کوتاه">
                <InputTextarea rows={2} value={form.summary} onChange={(e) => set("summary")(e.target.value)} placeholder="کاربر با این محتوا چه کاری می‌تواند انجام دهد؟"/>
            </Field>
            <Field label="برچسب‌ها" hint="با «،» جدا کنید. این برچسب‌ها در فیلتر و جست‌وجو استفاده می‌شوند.">
                <InputText value={form.tags} onChange={(e) => set("tags")(e.target.value)} placeholder="مثلاً: پروژه، ساخت پروژه، مایلستون"/>
            </Field>
            <Field label="سطح" full={false}>
                <Dropdown className="w-full" panelClassName="rtl" value={form.level} options={LEVELS} onChange={(e) => set("level")(e.value)}/>
            </Field>
            {video ? (
                <>
                    <Field label="مدت ویدئو" full={false}>
                        <InputText className="ltr" value={form.duration} onChange={(e) => set("duration")(e.target.value)} placeholder="05:18"/>
                    </Field>
                    <Field label="ویدئو" hint="لینک آپارات/یوتیوب یا فایل مستقیم را وارد کنید، یا فایل mp4/webm را مستقیم روی هاست دانلود (upload.zofino.ir) آپلود کنید.">
                        <InputText className="ltr" value={form.videoUrl} onChange={(e) => set("videoUrl")(e.target.value)} placeholder="https://"/>
                        <div className="flex align-items-center gap-2 mt-2 flex-wrap">
                            <input ref={videoFileRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={onVideoFile}/>
                            <Button type="button" className="p-button-outlined p-button-sm text-xs" icon="pi pi-upload" label="آپلود فایل ویدئو"
                                    disabled={videoUpload.busy} onClick={() => videoFileRef.current?.click()}/>
                            {videoUpload.busy && <ProgressBar className="flex-1" style={{height: "0.9rem"}} value={videoUpload.progress}/>}
                            {videoUpload.error && <small className="p-error">{videoUpload.error}</small>}
                        </div>
                    </Field>
                    <Field label="فصل‌های ویدئو" hint="هر فصل در یک خط: عنوان | زمان، مثلاً: ساخت فضای کاری | 01:12">
                        <InputTextarea rows={4} value={form.chapters} onChange={(e) => set("chapters")(e.target.value)}/>
                    </Field>
                </>
            ) : (
                <>
                    <Field label="زمان مطالعه" full={false}>
                        <InputText value={form.readTime} onChange={(e) => set("readTime")(e.target.value)} placeholder="۵ دقیقه"/>
                    </Field>
                    <Field label="سرفصل‌ها" hint="هر سرفصل در یک خط؛ در فهرست «در این راهنما» نمایش داده می‌شود.">
                        <InputTextarea rows={3} value={form.sections} onChange={(e) => set("sections")(e.target.value)}/>
                    </Field>
                    <Field label="متن محتوا" hint="می‌توانید متن را بنویسید، تصویر اضافه کنید یا فایل Word (.docx) را با متن و تصاویرش وارد کنید. تصاویر روی upload.zofino.ir ذخیره می‌شوند.">
                        <MarkdownEditor value={form.body} onChange={set("body")} rows={14} uploader={(file, options = {}) => uploadHelpMedia(file, options)}/>
                    </Field>
                </>
            )}
        </div>
    );

    const step2 = (
        <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-2">
                <b className="text-sm">ارتباط با محتوای مکمل</b>
                <span className="text-xs text-500">
                    {video
                        ? "مقاله‌ای را انتخاب کنید که این ویدئو مکمل آن است تا ویدئو داخل همان مقاله نمایش داده شود."
                        : "اگر این محتوا مقاله است، ویدئوی همان موضوع را انتخاب کنید تا داخل خود مقاله نمایش داده شود."}
                </span>
                <div className="flex align-items-center gap-2">
                    <Dropdown className="flex-1" panelClassName="rtl" showClear filter value={form.relatedSlug} options={relationOptions}
                              placeholder="بدون محتوای مکمل" onChange={(e) => set("relatedSlug")(e.value)}/>
                    <span className="text-primary">⇄</span>
                    <InputText className="flex-1" disabled value={form.title || "محتوای فعلی"}/>
                </div>
            </div>
            <div className="flex flex-column gap-2">
                <b className="text-sm">نمایش در بخش‌های برنامه</b>
                <span className="text-xs text-500">کنار بخش انتخاب‌شده در زوفینو یک آیکون «i» نمایش داده می‌شود که این محتوا را در تب جدید باز می‌کند.</span>
                <MultiSelect className="w-full" panelClassName="rtl" display="chip" value={form.appAnchors} options={APP_ANCHORS.map((a) => ({value: a.key, label: a.label}))}
                             placeholder="بدون اتصال به بخشی از برنامه" onChange={(e) => set("appAnchors")(e.value)}/>
            </div>
            <div className="flex flex-column gap-2">
                <b className="text-sm">کجا نمایش داده شود؟</b>
                <div className="grid">
                    {SURFACES.map((surface) => {
                        const on = Boolean(form.surfaces[surface.key]);
                        return (
                            <div key={surface.key} className="col-12 md:col-4">
                                <button type="button" onClick={() => set("surfaces")({...form.surfaces, [surface.key]: !on})}
                                        className={`w-full text-right border-1 border-round-lg p-3 cursor-pointer flex flex-column gap-1 ${on ? "border-primary surface-100" : "border-300 surface-card"}`}>
                                    <b className="text-sm">{surface.title}</b>
                                    <span className="text-xs text-500">{surface.description}</span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const step3 = (
        <div className="flex flex-column gap-3">
            <div className="grid m-0">
                <div className="col-12 md:col-8 surface-50 border-1 border-200 border-round-lg p-3 flex flex-column gap-2">
                    <span><Tag value={TYPE_LABELS[form.contentType]}/></span>
                    <h3 className="m-0">{form.title || "عنوان محتوای جدید"}</h3>
                    <span className="text-sm text-600">{form.summary || "پیش‌نمایش توضیح کوتاه محتوا در نتایج جست‌وجو و صفحه عمومی."}</span>
                    <div className="flex gap-2 flex-wrap text-xs text-500">
                        {(splitTags(form.tags).length ? splitTags(form.tags) : ["برچسب", "موضوع"]).map((tag) => <span key={tag}>#{tag}</span>)}
                    </div>
                    {category && <span className="text-xs text-500">دسته: {category.title}</span>}
                </div>
                <aside className="col-12 md:col-4 flex flex-column gap-2 text-xs p-3">
                    <b>پس از انتشار</b>
                    {form.surfaces.public && <span>✓ در جست‌وجوی عمومی</span>}
                    <span>✓ در دسته اصلی</span>
                    {form.surfaces.widget && <span>✓ در ویجت صفحه مرتبط</span>}
                    {form.relatedSlug && <span>✓ محتوای مکمل داخل صفحه</span>}
                    {form.appAnchors.length > 0 && <span>✓ آیکون راهنما در {form.appAnchors.length} بخش برنامه</span>}
                </aside>
            </div>
            <div className="surface-100 border-round-lg p-3 text-xs text-600">
                انتشار فوری است. بعداً می‌توانید محتوا را بدون حذف آمار به پیش‌نویس برگردانید.
                {item && <span> وضعیت فعلی: {STATUS_LABELS[item.contentStatus]}.</span>}
            </div>
        </div>
    );

    const footer = (
        <div className="flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="flex gap-2">
                <Button className="p-button-text text-xs" label="انصراف" onClick={onHide}/>
                {item && <Button className="p-button-text p-button-danger text-xs" label="حذف" disabled={saving} onClick={remove}/>}
            </div>
            <div className="flex flex-row-reverse gap-2">
                {step < 3 ? (
                    <Button className="text-xs" label="ادامه" onClick={next}/>
                ) : (
                    <>
                        <Button className="text-xs" label="انتشار محتوا" loading={saving} onClick={() => save("published")}/>
                        <Button className="p-button-outlined text-xs" label="ارسال برای بازبینی" disabled={saving} onClick={() => save("review")}/>
                        <Button className="p-button-text text-xs" label="ذخیره پیش‌نویس" disabled={saving} onClick={() => save("draft")}/>
                    </>
                )}
                {step > 1 && <Button className="p-button-outlined text-xs" label="مرحله قبل" onClick={() => setStep(step - 1)}/>}
            </div>
        </div>
    );

    return (
        <Dialog visible={visible} onHide={onHide} className="rtl w-11 md:w-8 xl:w-6" footer={footer}
                header={<div className="flex flex-column"><span>{item ? "ویرایش محتوا" : "محتوای جدید"}</span><small className="text-400 font-normal">گام {step} از ۳</small></div>}>
            <div className="flex gap-2 mb-3 text-xs flex-wrap">
                {STEP_TITLES.map((title, index) => (
                    <span key={title} className={`flex-1 text-center p-2 border-round ${step >= index + 1 ? "bg-primary text-white" : "surface-100 text-500"}`}>{title}</span>
                ))}
            </div>
            {step === 1 ? step1 : step === 2 ? step2 : step3}
            {error && <small className="p-error block mt-2">{error}</small>}
        </Dialog>
    );
}

